import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  StudyDetailLayout,
  StudyErrorBoundary,
  studyDetailLoader,
  studyDetailSearchSchema,
} from '../simops/-components/StudyDetailLayout';
import { isEvalToolStudy } from '../simops/-data/constants';

/**
 * The EvalTool mounting of the study detail shell — the same component
 * `/simops/$studyId` mounts. The prefix is what makes the header link back to
 * `/evaltool` and keeps the Results tabs under it (`useStudyLibrary`).
 *
 * It exists so that reading a study's results from EvalTool has an EvalTool
 * URL. Before this the address said `/simops/...` and carried the product in a
 * search param (`?origin=evaltool`), which a shared or retyped link dropped.
 */
function EvalToolStudyRoute() {
  const { studyId } = Route.useParams();
  const search = Route.useSearch();
  return <StudyDetailLayout studyId={studyId} search={search} />;
}

export const Route = createFileRoute('/_app/evaltool/$studyId')({
  validateSearch: studyDetailSearchSchema,
  // EvalTool is the finished-work library (`isEvalToolStudy`), so a study that
  // is still meshing or simulating forwards to the SimOps mounting
  // rather than rendering those steps under a product with no place for them.
  //
  // In the LOADER, not in the component: a render-time `<Navigate>` re-reads
  // the status on every render, so a reviewer who approved a MOBO review would
  // be thrown out to `/simops` the moment their own action flipped the study to
  // `running` — with `replace`, so Back could not bring them back. The loader
  // runs on entry and does not re-run on a status change.
  //
  // A study that fails to load falls through to the component, which owns the
  // not-found and load-failed cards.
  loader: async ({ context: { queryClient }, params: { studyId } }) => {
    const study = await studyDetailLoader({ queryClient, studyId });
    if (study && !isEvalToolStudy(study.status)) {
      throw redirect({
        to: '/simops/$studyId',
        params: { studyId },
        search: (prev) => prev,
        replace: true,
      });
    }
  },
  component: EvalToolStudyRoute,
  errorComponent: StudyErrorBoundary,
});
